import { useState } from "react";
import { ChevronLeft, ChevronDown, ChevronUp, ExternalLink, FileText, Video, Link2, PlayCircle } from "lucide-react";

// ─── Video embed utility ───────────────────────────────────────────────────────
// Returns iframe src for YouTube/Vimeo/Loom, native src for direct files and
// Wibiz Media Storage (storage.googleapis.com/msgsndr/…), or null for other links.
function getVideoEmbed(url: string): { type: "iframe"; src: string } | { type: "native"; src: string } | null {
  if (!url) return null;

  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return { type: "iframe", src: `https://www.youtube.com/embed/${ytMatch[1]}?rel=0` };

  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) return { type: "iframe", src: `https://player.vimeo.com/video/${vimeoMatch[1]}` };

  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return { type: "iframe", src: `https://www.loom.com/embed/${loomMatch[1]}` };

  // Direct video files and Wibiz Media Storage (GHL msgsndr bucket)
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url) || url.includes("storage.googleapis.com")) {
    return { type: "native", src: url };
  }

  return null;
}

const TYPE_COLORS: Record<string, string> = {
  pdf: "#D93025",
  video: "#1A6FD4",
  link: "#0FA8A8",
  template: "#7B4FD4",
  pptx: "#E8620A",
  image: "#7B4FD4",
};

interface ResourceModulePanelProps {
  folder: any;
  materials: any[];
  color: string;
  onBack: () => void;
}

export function ResourceModulePanel({ folder, materials, color, onBack }: ResourceModulePanelProps) {
  const videos = materials.filter(m => m.type === "video");
  const attachments = materials.filter(m => m.type !== "video");
  const [activeVideoIdx, setActiveVideoIdx] = useState(0);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<number, boolean>>({});

  const toggleDescription = (id: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedDescriptions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const activeVideo = videos[activeVideoIdx] ?? null;
  const embed = activeVideo ? getVideoEmbed(activeVideo.url) : null;

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Resources
      </button>

      {/* Module header */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm mb-6">
        {folder.coverImageUrl && (
          <div className="aspect-video relative overflow-hidden">
            <img src={folder.coverImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover rounded-t-2xl" />
          </div>
        )}
        <div className="p-5">
          <h2 className="text-lg font-bold text-[#2C2723]">{folder.name}</h2>
          {folder.description && (
            <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{folder.description.replace(/<br\s*\/?>/gi, "\n")}</p>
          )}
          <p className="text-xs text-gray-400 mt-2">
            {videos.length > 0 && `${videos.length} video${videos.length !== 1 ? "s" : ""}`}
            {videos.length > 0 && attachments.length > 0 && " · "}
            {attachments.length > 0 && `${attachments.length} resource${attachments.length !== 1 ? "s" : ""}`}
          </p>
        </div>
      </div>

      {/* Video section */}
      {videos.length > 0 && (
        <div className="mb-8">
          <div className={videos.length > 1 ? "lg:grid lg:grid-cols-3 lg:gap-5" : ""}>
            {/* Player */}
            <div className={videos.length > 1 ? "lg:col-span-2 mb-4 lg:mb-0" : ""}>
              {embed ? (
                embed.type === "iframe" ? (
                  <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingTop: "56.25%" }}>
                    <iframe
                      src={embed.src}
                      className="absolute inset-0 w-full h-full"
                      allowFullScreen
                      allow="autoplay; encrypted-media; picture-in-picture"
                      title={activeVideo.title}
                    />
                  </div>
                ) : (
                  <video
                    key={embed.src}
                    src={embed.src}
                    controls
                    className="w-full rounded-xl bg-black"
                    style={{ maxHeight: "380px" }}
                  />
                )
              ) : (
                <div
                  className="w-full rounded-xl bg-[#F4EEE3] border border-[#EBE2D4] flex flex-col items-center justify-center gap-3 py-16"
                >
                  <PlayCircle className="w-10 h-10 text-gray-300" />
                  <p className="text-sm text-gray-400">This video opens in a new tab.</p>
                  <a
                    href={activeVideo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm font-semibold text-[#1A6FD4] hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Open video
                  </a>
                </div>
              )}
              {activeVideo && (
                <div className="mt-3 px-1">
                  <div className="font-semibold text-[#2C2723] text-sm">{activeVideo.title}</div>
                  {activeVideo.description && (
                    <p className="text-xs text-gray-700 mt-0.5 whitespace-pre-wrap">{activeVideo.description.replace(/<br\s*\/?>/gi, "\n")}</p>
                  )}
                </div>
              )}
            </div>

            {/* Playlist (only shown when > 1 video) */}
            {videos.length > 1 && (
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                  In this module ({videos.length})
                </p>
                <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                  {videos.map((v: any, idx: number) => (
                    <button
                      key={v.id}
                      onClick={() => setActiveVideoIdx(idx)}
                      className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all ${
                        idx === activeVideoIdx
                          ? "bg-[#1A6FD4]/10 border-[#1A6FD4]/30"
                          : "bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <span
                          className={`text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            idx === activeVideoIdx
                              ? "bg-[#1A6FD4] text-white"
                              : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          {idx + 1}
                        </span>
                        <span
                          className={`text-xs font-medium line-clamp-2 ${
                            idx === activeVideoIdx ? "text-[#1A6FD4]" : "text-gray-700"
                          }`}
                        >
                          {v.title}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Attachments / non-video resources */}
      {attachments.length > 0 && (
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
            {videos.length > 0 ? "Additional Resources" : "Resources"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {attachments.map((m: any) => {
              const mc = TYPE_COLORS[m.type] ?? color;
              const TypeIcon = m.type === "video" ? Video : m.type === "link" ? Link2 : FileText;
              const href = m.type === "pdf"
                ? `https://docs.google.com/viewer?url=${encodeURIComponent(m.url)}&embedded=true`
                : m.url;
              return (
                <div
                  key={m.id}
                  className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md hover:border-gray-200 transition-all group"
                >
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${mc}15` }}
                    >
                      <TypeIcon className="w-4 h-4" style={{ color: mc }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm group-hover:text-[#84ab4a] transition-colors line-clamp-1">
                        {m.title}
                      </div>
                      {m.description && (
                        <div className={`text-xs text-gray-700 mt-0.5 whitespace-pre-wrap ${expandedDescriptions[m.id] ? "" : "line-clamp-2"}`}>
                          {m.description.replace(/<br\s*\/?>/gi, "\n")}
                        </div>
                      )}
                      <span
                        className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full mt-1.5 inline-block"
                        style={{ background: `${mc}15`, color: mc }}
                      >
                        {m.type}
                      </span>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors" />
                  </a>
                  {m.description && m.description.length > 80 && (
                    <button
                      onClick={(e) => toggleDescription(m.id, e)}
                      className="w-full flex items-center justify-center gap-1 px-4 pb-2.5 text-[11px] font-semibold text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {expandedDescriptions[m.id] ? (
                        <><ChevronUp className="w-3 h-3" /> Show less</>
                      ) : (
                        <><ChevronDown className="w-3 h-3" /> View more</>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty module */}
      {materials.length === 0 && (
        <div className="bg-[#FBF7F0] border border-[#EBE2D4] rounded-2xl p-10 text-center">
          <p className="text-sm text-gray-800">No resources in this module yet. Check back soon.</p>
        </div>
      )}
    </div>
  );
}
